import Image from 'next/image';
import Text from '@components/atoms/Text';
import { SearchResultsProps } from '@interfaces/search-component.types';
import NoResultsState from '@components/molecules/NoResultsState';
import styles from '../../organisms/SearchComponent/SearchComponent.module.scss';
import assetLoader, { s3UrlLoader } from '@utils/internalImageLoder';
import { toKebabCase } from '@utils/stringFormatters';
import Loader from '@components/atoms/Loader';
import {useIsMobile} from '@hooks/use-mobile';
import { trackException } from '@utils/exceptionTracker';
import { PAGE_TYPES } from '@constants/pageTypes';

export const SearchResults = ({
  searchResults,
  searchQuery,
  activeTab,
  onItemClick,
  variant,
  isLoading
}: SearchResultsProps & { isLoading: boolean }) => {

  const isMobile = useIsMobile();

  if (isLoading) {
    return  <Loader
              text="Searching..."
              theme={ isMobile ? 'light' : 'dark'}
              variant="center"
              size="medium"
            />;
  }

  if (!searchResults) {
    return null;
  }

  const rawCarsData = searchResults.carsData;
  const rawBlogsData = searchResults.blogsData;
  
  const safeCarsData = Array.isArray(rawCarsData) ? rawCarsData : [];
  const safeBlogsData = Array.isArray(rawBlogsData) ? rawBlogsData : [];

  const currentData = activeTab === 'cars' ? safeCarsData : safeBlogsData;


  const exceptionEvent = async () => {
    await trackException(
      "system_error",
      "No data available for search",
       PAGE_TYPES.BUY_HOMEPAGE,
      {
        exception_type: "system_error",
        exception_name: "view_no_search_result",
        search_query: searchQuery
      }
    );
  }

  if (searchQuery.trim() && currentData.length === 0) {
    exceptionEvent();
    return (
      <NoResultsState
        activeTab={activeTab}
        searchQuery={searchQuery}
        variant={variant}
      />
    );
  }

  if (currentData.length === 0) {
    return null;
  }

  const isBrandCollection = (item: any) => {
    return item.name?.toLowerCase().includes('all ') ||
      item.name?.toLowerCase().includes(' cars') ||
      !item.model_name;
  };

  return (
    <div className={styles.sectionSearchResult}>
      <Text className={styles.sectionTitle}>
        {!searchQuery.trim()
          ? (activeTab === 'cars' ? 'Recommended Cars' : 'Latest Articles')
          : 'Search Results'
        }
      </Text>
      {currentData?.map((item: any, index: number) => (
        <div
          key={index}
          className={styles.resultItem}
          onClick={() => onItemClick(item)}
        >
          {activeTab === 'cars' ? (
            <div className={styles.carItem}>
              <Image
                src={
                  isBrandCollection(item)
                    ? `${assetLoader()}/icons/${variant === 'desktop' ? 'car-brands-light' : 'car-brands-dark'}/${item.make_name}.svg`
                    : `${s3UrlLoader()}/${toKebabCase(item.make_name)}/${toKebabCase(item.model_name)}/default/Hero-Transparent.png`
                }
                alt={item.name}
                width={48}
                height={48}
                className={`${styles.carImage} ${isBrandCollection(item) ? styles.brandImage : ''}`}
                loading='lazy'
                onError={(e) => {
                  (e.target as HTMLImageElement).src = `${
                    isBrandCollection(item)
                    ? `${assetLoader()}/icons/fallbackCarLogo.svg`
                    : `${assetLoader()}/images/placeHolder-FallbackImage.png`
                  }`;
                }}
              />
              <span>{item.name}</span>
            </div>
          ) : (
            <div className={styles.blogItem}>
              <Image
                src={item.image_url}
                alt={item.name}
                width={variant === 'desktop' ? 96 : 48}
                height={48}
                className={styles.blogImage}
                loading='lazy'
              />
              <div className={styles.blogInfo}>
                <span className={styles.blogTitle}>{item.name}</span>
                {item.published_date && <span className={styles.blogDate}>{item.published_date}</span>}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default SearchResults; 